import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useDirection from "hooks/useDirection";
import { destroy } from "redux-form";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  getPartnerTokenFromStorage,
  isYeloAppSession,
  PARTNER_CHECKOUT_STORAGE_KEY,
  partnerHomePath,
  withPartnerToken,
} from "utils/partnerSession.util";
import { notifyApp } from "utils/yeloAppBridge.util";
import BookingService from "services/booking.service";
import DateService from "utils/date.util";
import { bookingTimeFormatOf } from "utils/timeFormat.util";
import "./PaymentForm.scss";
import {
  markNextBookingAsBookAgain,
  trackBookingConfirmed,
} from "services/mixpanel.service";
import { shouldEnableYeloAppAnalytics } from "utils/analytics.util";

const CheckIcon = () => (
  <svg className="ico" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7" />
  </svg>
);

const ClockIcon = () => (
  <svg className="ico" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 1.8" />
  </svg>
);

type PartnerPaymentSuccessProps = {
  bookingId: string;
};

export default function PartnerPaymentSuccess({ bookingId }: PartnerPaymentSuccessProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation("translation");
  const direction = useDirection();
  const booking = useAppSelector((state) => state.booking.booking);

  const trip = booking && String(booking._id) === String(bookingId) ? booking : null;

  useEffect(() => {
    if (shouldEnableYeloAppAnalytics() && bookingId) {
      trackBookingConfirmed(bookingId);
    }
    if (isYeloAppSession()) {
      notifyApp("payment_complete");
    }
    localStorage.removeItem(PARTNER_CHECKOUT_STORAGE_KEY);
    localStorage.removeItem("classsPrice");
    localStorage.removeItem("classesPrices");
    localStorage.removeItem("SearchForm");
    localStorage.removeItem("SearchTripForm");
    localStorage.removeItem("SearchHourlyForm");
    localStorage.removeItem("AdditionalInfoForm");
    localStorage.removeItem("BookingFormPayload");
    localStorage.removeItem("PickUpForm");
    dispatch(destroy("SearchForm", "AdditionalInfoForm", "PickUpForm"));
  }, [dispatch]);

  useEffect(() => {
    if (!bookingId) return;
    BookingService.getBookingById(bookingId, getPartnerTokenFromStorage());
  }, [bookingId]);

  const viewBooking = () => {
    /* No list behind this one — the journey was just booked, so back from
       the details screen returns to home rather than to Bookings. Replaced,
       not pushed: the phone's back button returned to this screen and fired
       payment_complete to the app a second time. */
    navigate(withPartnerToken(`/booking/${bookingId}?from=success`), {
      replace: true,
    });
  };

  return (
    <div className="yp-payment" dir={direction}>
      <div className="yp-payment__state">
        <div className="yp-payment__success-card">
          <div className="yp-payment__success-icon">
            <span className="yp-payment__success-ring" aria-hidden="true" />
            <span className="yp-payment__success-circle">
              <CheckIcon />
            </span>
          </div>

          <h1 className="yp-payment__success-title">
            {t("paymentSuccessful", "Payment successful")}
          </h1>
          <p className="yp-payment__success-body">
            {t("YourBookingPlacedSuccessfully")}
          </p>

          {trip?.booking_number ? (
            <span className="yp-payment__success-chip">
              {t("bookingId")} #{trip.booking_number}
            </span>
          ) : null}

          {trip ? (
            <div className="yp-payment__success-trip">
              <div className="yp-payment__success-trip-head">
                <span className="yp-payment__success-trip-class">
                  {trip?.class_details?.name}
                </span>
                <span className="yp-payment__success-trip-when">
                  <ClockIcon />
                  {DateService.getShortDateString(trip?.date)},{" "}
                  {DateService.getDisplayTimeString(
                    trip?.date,
                    bookingTimeFormatOf(trip)
                  )}
                </span>
              </div>

              <div className="yp-payment__success-leg">
                <span className="yp-payment__success-pin">
                  <span className="yp-payment__success-pin-line" aria-hidden="true" />
                  <b />
                </span>
                <div className="yp-payment__success-leg-row">
                  <strong>{trip?.pickup?.title}</strong>
                  <em>{trip?.pickup?.address}</em>
                </div>

                <span
                  className={`yp-payment__success-pin is-end${
                    trip?.trip_type === "hourly" ? " is-hourly" : ""
                  }`}
                >
                  <b />
                </span>
                <div className="yp-payment__success-leg-row">
                  <strong>
                    {trip?.trip_type === "hourly"
                      ? `${trip?.duration} ${t("hours")}`
                      : trip?.destination?.title}
                  </strong>
                  <em>
                    {trip?.trip_type === "hourly"
                      ? t("hourlyIncludedKm", {
                          km: Number(trip?.duration || 0) * 40,
                          defaultValue: "Incl. {{km}} km",
                        })
                      : trip?.destination?.address}
                  </em>
                </div>
              </div>

              {Number(trip?.price) > 0 ? (
                <div className="yp-payment__success-total">
                  <span>{t("total")} {t("price")}</span>
                  <b dir="ltr">
                    {Number(trip.price).toFixed(2)}{" "}
                    {trip?.price_details?.currency_sign}
                  </b>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="yp-payment__success-actions">
            <button
              type="button"
              className="yp-payment__submit"
              onClick={viewBooking}
            >
              {t("viewBooking")}
            </button>
            <button
              type="button"
              className="yp-payment__submit yp-payment__submit--outline"
              onClick={() => {
                if (shouldEnableYeloAppAnalytics()) markNextBookingAsBookAgain();
                navigate(partnerHomePath(), { replace: true });
              }}
            >
              {t("bookYourReturnRide")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
